/**
 * A tab strip that scrolls sideways instead of wrapping onto a second line.
 *
 * `.crm-tabs` wraps by default, which is right for a filter row of three or
 * four pills. It is wrong for the strips that name the PARTS of a record: the
 * project page carries eleven tabs, settings up to twelve, and a campaign six
 * with badges. Those rows folded on an ordinary laptop, and a folded strip is
 * worse than a long one - the leftover pills sit alone under the first line and
 * read as a second, unrelated group, and the whole page below them shifts down
 * by a row the moment a count appears and one more pill no longer fits.
 *
 * So a rail keeps ONE line and lets it scroll. Three things have to be true for
 * that to be usable rather than merely tidy:
 *
 * 1. The edge that still has something behind it fades out, so the row says it
 *    continues. The fade is a mask driven by two classes, the same way the
 *    sidebar's vertical one works (`.crm-side-scroll` in `index.css`) - one
 *    idea, one vocabulary, and no permanently faded edge pretending there is
 *    more when there is not.
 * 2. A pointer that has no sideways gesture gets an arrow over each live fade.
 *    A trackpad and a touch screen scroll this row without help; a wheel mouse
 *    cannot, and hijacking the vertical wheel would steal the page's own scroll
 *    from anybody whose cursor happened to be over the tabs.
 * 3. The selected pill is pulled into view when it changes. A tab opened from
 *    the URL (`?tab=activity`) or from another page's button is otherwise
 *    selected off-screen, which is indistinguishable from nothing happening.
 *
 * The scroller is the element that carries `.crm-tabs`, so everything the pills
 * already know about themselves keeps working. The wrapper exists only to hang
 * the arrows on, because a button inside the scroller would scroll away with it.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";

/** How much of a pill is left uncovered beside the fade when the rail scrolls
 *  a selection into view. Wider than the fade itself, so the pill lands fully
 *  lit rather than half under the gradient. */
const KEEP = 40;

/**
 * The one place that moves the rail.
 *
 * A plain `scrollLeft` assignment rather than `scrollTo({behavior})`, and the
 * animation comes from `scroll-behavior` in the stylesheet instead. Two reasons,
 * and the second is the real one: an assignment is understood by every engine,
 * where an ignored options object leaves the rail sitting exactly where it was
 * with nothing to catch; and a stylesheet switch is what `prefers-reduced-motion`
 * can turn off, which a hard-coded `"smooth"` in the callback cannot.
 *
 * `instant` overrides that switch for one assignment. The first placement has to
 * be instant - a rail that was never on screen has no movement worth watching,
 * and animating it would show the operator the wrong tab for the length of the
 * animation.
 */
function slide(el: HTMLElement, left: number, instant?: boolean) {
  if (instant) el.style.scrollBehavior = "auto";
  el.scrollLeft = Math.max(0, left);
  if (instant) el.style.scrollBehavior = "";
}

export function TabRail({
  children,
  className,
  tabsClassName,
  /**
   * The wrapper's tag, and the wrapper is the one that changes because the
   * scroller has a job the semantics must not fight over: a row of routed links
   * is navigation and wants a `nav` around it, while a row of buttons that swaps
   * a panel below is a `tablist`. Putting the choice on the OUTER element keeps
   * the scrolling div plain, which is what lets its ref stay honestly typed.
   */
  as = "div",
  role = "tablist",
  label,
}: {
  children: ReactNode;
  className?: string;
  /** Classes for the scrolling `.crm-tabs` element itself. The pill modifiers
   *  live here and not on the wrapper: `.crm-tabs-sm > button` reaches DIRECT
   *  children, so a modifier parked one box out styles nothing at all. */
  tabsClassName?: string;
  as?: "div" | "nav";
  role?: string;
  label?: string;
}) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ start: false, end: false });
  // The pill that was selected last time this rail looked. Kept so the strip is
  // only dragged under the operator when the selection actually MOVED - not on
  // every re-render, which would fight a hand that had scrolled it elsewhere.
  const seen = useRef<Element | null>(null);

  // The same object back when nothing moved, and that is not a micro-optimisation
  // - it is what stops a loop. The observer below is rebuilt on every render, a
  // fresh ResizeObserver fires once as soon as it observes anything, and a new
  // `{start, end}` object every time would make that fire a render, which would
  // rebuild the observer, which would fire again.
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const start = el.scrollLeft > 1;
    const end = el.scrollLeft < max - 1;
    setFade((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  // Both observers are needed and they answer different questions. The element
  // resizes when the window or the sidebar does; the CONTENT resizes when a
  // count arrives, a permission hides a tab or a webfont finally swaps - and
  // that leaves the element exactly the same width, so a plain ResizeObserver
  // on the rail never hears about it.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    return () => ro.disconnect();
  });

  // The selected pill, kept in view. `scrollIntoView` is deliberately not used:
  // its block axis would scroll the PAGE to satisfy a row that is already
  // vertically visible, which on a long record page jumps the reader somewhere
  // they did not ask to be.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const on = el.querySelector('[aria-selected="true"], .is-on, [aria-current="page"]');
    if (!on || on === seen.current) return;
    const first = seen.current === null;
    seen.current = on;
    const box = on as HTMLElement;
    const left = box.offsetLeft;
    const right = left + box.offsetWidth;
    // Instant on the first look, because a smooth scroll from a rail that was
    // never on screen is an animation nobody watches; smooth afterwards, where
    // the movement is what says the strip has more of itself to show.
    if (left < el.scrollLeft + KEEP) {
      slide(el, left - KEEP, first);
    } else if (right > el.scrollLeft + el.clientWidth - KEEP) {
      slide(el, right - el.clientWidth + KEEP, first);
    }
  });

  // Roughly two pills, so one press moves the row by a readable step rather
  // than by a pixel count nobody can predict.
  const nudge = (dir: -1 | 1) => {
    const el = ref.current;
    if (!el) return;
    slide(el, el.scrollLeft + dir * Math.max(160, el.clientWidth * 0.6));
  };

  const Wrap = as;
  const nav = as === "nav";
  return (
    <Wrap className={cn("crm-rail", className)} aria-label={nav ? label : undefined}>
      <div
        ref={ref}
        className={cn("crm-tabs crm-tabs-rail", tabsClassName, fade.start && "is-fs", fade.end && "is-fe")}
        role={nav ? undefined : role}
        aria-label={nav ? undefined : label}
        onScroll={update}
      >
        {children}
      </div>
      {/* Both arrows are always here, and WHEN each one is seen is left to the
          stylesheet (`.crm-rail:has(.is-fs)`, and a coarse pointer never sees
          either). The condition already exists as the fade classes above; asking
          React the same question a second time would be two rules that have to
          agree, and the day they stop agreeing is the day an arrow appears over
          a row with nothing behind it.

          `aria-hidden` because every tab is already in the tab order and reached
          by keyboard - these two are a pointer convenience and must not become
          two more stops on the way there. */}
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        className="crm-rail-arw crm-rail-arw-s"
        title={t("common.prev")}
        onClick={() => nudge(-1)}
      >
        <ChevronLeft className="size-4" />
      </button>
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        className="crm-rail-arw crm-rail-arw-e"
        title={t("common.next")}
        onClick={() => nudge(1)}
      >
        <ChevronRight className="size-4" />
      </button>
    </Wrap>
  );
}
